import React from "react";
import Footer from "../components/Footer";
import NewNavbar from "../components/NewNavbar";
import { Helmet } from "@dr.pogodin/react-helmet";

const StaticContentPage = ({
  title,
  children,
  seoTitle,
  seoDescription,
  canonicalPath,
}) => {
  const isStringContent = typeof children === "string";
  const content = isStringContent ? children : "";

  const contactLabels = [
    "Business Name",
    "Website",
    "Phone",
    "Email",
    "Address",
    "Customer Service",
    "Response Time",
  ];

  const isNumberedLine = (line) => /^\d+\.\s+/.test(line);
  const isBulletLine = (line) => /^-\s+/.test(line);

  const isContactDetailLine = (line) => {
    return contactLabels.some((label) =>
      line.toLowerCase().startsWith(`${label.toLowerCase()}:`),
    );
  };

  const isHeadingLine = (line) => {
    const trimmed = line.trim();

    if (!trimmed) return false;
    if (isNumberedLine(trimmed)) return false;
    if (isBulletLine(trimmed)) return false;
    if (isContactDetailLine(trimmed)) return false;

    const lower = trimmed.toLowerCase();

    const nonHeadingStarts = [
      "if ",
      "we ",
      "you ",
      "yes,",
      "no,",
      "after ",
      "once ",
      "please ",
      "this ",
      "these ",
      "your ",
      "some ",
      "in ",
      "at ",
      "from ",
      "to ",
    ];

    const isLikelySentence =
      trimmed.endsWith(".") ||
      trimmed.endsWith(",") ||
      trimmed.length > 95 ||
      nonHeadingStarts.some((start) => lower.startsWith(start));

    if (isLikelySentence) return false;

    return true;
  };

  const renderInlineText = (text) => {
    const labelMatch = text.match(/^([^:]{2,45}):\s*(.+)$/);

    if (labelMatch) {
      return (
        <>
          <span className="font-medium text-[#111111]">{labelMatch[1]}:</span>{" "}
          <span className="text-[#4F4F4F]">{labelMatch[2]}</span>
        </>
      );
    }

    return text;
  };

  const renderContactDetails = (contactLines, keyPrefix) => {
    return (
      <div
        key={keyPrefix}
        className="
          my-7 rounded-[6px] border border-[#E1E1E1] bg-white
          p-5 sm:p-6
        "
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
          {contactLines.map((line, itemIndex) => {
            const [label, ...valueParts] = line.split(":");
            const value = valueParts.join(":").trim();
            const normalizedLabel = label.trim().toLowerCase();

            return (
              <div
                key={`${keyPrefix}-${itemIndex}`}
                className={
                  normalizedLabel === "address" ||
                  normalizedLabel === "customer service"
                    ? "sm:col-span-2"
                    : ""
                }
              >
                <p
                  className="
                    mb-1 font-spaceMono text-[10px] font-normal uppercase
                    tracking-[0.14em] text-[#777777]
                  "
                >
                  {label}
                </p>

                {normalizedLabel === "phone" ? (
                  <a
                    href={`tel:${value.replace(/\s/g, "")}`}
                    className="
                      font-inter text-[14px] leading-[160%] text-[#111111]
                      underline-offset-4 hover:underline
                    "
                  >
                    {value}
                  </a>
                ) : normalizedLabel === "email" ? (
                  <a
                    href={`mailto:${value}`}
                    className="
                      font-inter text-[14px] leading-[160%] text-[#111111]
                      underline-offset-4 hover:underline
                    "
                  >
                    {value}
                  </a>
                ) : normalizedLabel === "website" ? (
                  <a
                    href={`https://${value.replace(/^https?:\/\//, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="
                      font-inter text-[14px] leading-[160%] text-[#111111]
                      underline-offset-4 hover:underline
                    "
                  >
                    {value}
                  </a>
                ) : (
                  <p className="font-inter text-[14px] leading-[160%] text-[#333333]">
                    {value}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderContent = (rawContent) => {
    const lines = rawContent
      .split("\n")
      .map((line) => line.trim())
      .filter((line, index, arr) => {
        if (line) return true;
        return arr[index - 1] && arr[index + 1];
      });

    const elements = [];
    let index = 0;

    while (index < lines.length) {
      const line = lines[index];

      if (!line) {
        index += 1;
        continue;
      }

      if (isContactDetailLine(line)) {
        const contactLines = [];

        while (index < lines.length && isContactDetailLine(lines[index])) {
          contactLines.push(lines[index]);
          index += 1;
        }

        elements.push(renderContactDetails(contactLines, `contact-${index}`));
        continue;
      }

      if (isBulletLine(line)) {
        const bulletItems = [];

        while (index < lines.length && isBulletLine(lines[index])) {
          bulletItems.push(lines[index].replace(/^-\s+/, ""));
          index += 1;
        }

        elements.push(
          <ul
            key={`ul-${index}`}
            className="
              my-5 space-y-2 border-l border-[#D8D8D8] pl-5
              font-inter text-[14px] leading-[165%] text-[#333333]
            "
          >
            {bulletItems.map((item, itemIndex) => (
              <li key={itemIndex} className="relative pl-3">
                <span className="absolute left-[-2px] top-[0.75em] h-[3px] w-[3px] rounded-full bg-[#111111]" />
                {renderInlineText(item)}
              </li>
            ))}
          </ul>,
        );

        continue;
      }

      if (isNumberedLine(line)) {
        const numberMatch = line.match(/^(\d+)\.\s+(.+)$/);

        elements.push(
          <div
            key={`number-${index}`}
            className="
              mt-7 mb-3 flex gap-4 rounded-[6px] border border-[#E1E1E1]
              bg-[#FAFAFA] px-4 py-3
            "
          >
            <span className="font-spaceMono text-[11px] text-[#777777]">
              {String(numberMatch?.[1] || "").padStart(2, "0")}
            </span>
            <p className="font-inter text-[15px] font-medium leading-[150%] text-[#111111]">
              {numberMatch?.[2]}
            </p>
          </div>,
        );

        index += 1;
        continue;
      }

      if (isHeadingLine(line)) {
        elements.push(
          <h2
            key={`heading-${index}`}
            className="
              mt-9 mb-3 border-t border-[#D9D9D9] pt-6
              font-inter text-[20px] font-medium leading-[130%] text-[#111111]
              sm:text-[22px]
            "
          >
            {line}
          </h2>,
        );

        index += 1;
        continue;
      }

      elements.push(
        <p
          key={`paragraph-${index}`}
          className="
            mb-4 font-inter text-[14px] leading-[170%]
            text-[#3F3F3F] sm:text-[15px]
          "
        >
          {renderInlineText(line)}
        </p>,
      );

      index += 1;
    }

    return elements;
  };

  const finalSeoTitle = seoTitle || `${title} | Eveness`;
  const finalSeoDescription =
    seoDescription ||
    "Discover personalized fashion, curated style guidance, and AI-powered shopping experiences on Eveness.";
  const canonicalUrl = `https://www.eveness.ai${canonicalPath || ""}`;

  return (
    <div className="min-h-screen bg-[#F7F7F7] text-[#111111]">
      <Helmet>
        <title>{finalSeoTitle}</title>

        <meta name="title" content={finalSeoTitle} />
        <meta name="description" content={finalSeoDescription} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={finalSeoTitle} />
        <meta property="og:description" content={finalSeoDescription} />
        <meta property="og:url" content={canonicalUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={finalSeoTitle} />
        <meta name="twitter:description" content={finalSeoDescription} />

        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <NewNavbar />

      <main className="min-h-screen">
        <section className="mx-auto w-full max-w-[1540px] px-4 pt-10 pb-16 md:px-6">
          <div
            className="
              rounded-[6px] border border-[#DCDCDC] bg-white
              px-4 py-7 shadow-[0_1px_2px_rgba(0,0,0,0.03)]
              sm:px-6 md:px-8 md:py-9
            "
          >
            <div className="mb-4 pb-2">
              {title && (
                <h1
                  className="
                    font-inter text-[36px] font-semibold leading-[100%]
                    tracking-[-0.04em] text-black
                    sm:text-[48px] md:text-[56px]
                  "
                >
                  {title}
                </h1>
              )}

            </div>
            <article
              className="
                font-inter text-[15px] font-normal
                leading-[170%] text-[#333333]
              "
            >
              {isStringContent ? renderContent(content) : children}
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default StaticContentPage;
